import { Check, CheckCheck, Clock, AlertCircle, Package } from "lucide-react";
import type { ConversationMessage } from "../../../services/whatsapp/whatsappService";

interface MessageBubbleProps {
  message: ConversationMessage;
}

const MessageStatus = ({ status }: { status?: string }) => {
  switch (status) {
    case "sending":
      return <Clock className="w-3.5 h-3.5 text-[#667781]" />;
    case "failed":
      return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
    case "sent":
      return <Check className="w-3.5 h-3.5 text-[#667781]" />;
    case "delivered":
      return <CheckCheck className="w-3.5 h-3.5 text-[#667781]" />;
    case "read":
      return <CheckCheck className="w-3.5 h-3.5 text-[#53BDEB]" />;
    default:
      // Older server messages did not include delivery status.
      return <Check className="w-3.5 h-3.5 text-[#667781]" />;
  }
};

const TemplateBubble = ({ message }: MessageBubbleProps) => {
  const timeStr = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div className="flex justify-end">
      <div className="max-w-[75%] rounded-lg rounded-tr-none shadow-sm overflow-hidden">
        {/* Template card */}
        <div className="bg-[#D9FDD3] px-3 pt-2.5 pb-1.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Package className="w-3.5 h-3.5 text-[#667781]" />
            <span className="text-[11px] font-semibold text-[#667781] uppercase tracking-wide">
              Confirmacion de pedido
            </span>
          </div>
          {message.media_url && (
            <img
              src={message.media_url}
              alt=""
              className="mb-2 max-h-56 w-full rounded-md object-cover"
            />
          )}
          <p className="whitespace-pre-wrap break-words text-sm leading-[1.5] text-[#111B21]">
            {message.content}
          </p>
          <div className="flex items-center gap-1 mt-1 justify-end">
            <span className="text-[10px] text-[#667781]">{timeStr}</span>
            <MessageStatus status={message.status} />
          </div>
        </div>
        {/* Template buttons */}
        <div className="bg-[#D9FDD3] border-t border-[#c5e8c0]">
          <div className="flex divide-x divide-[#c5e8c0]">
            <div className="flex-1 py-1.5 text-center text-[12px] font-medium text-[#00A884]">
              Confirmar Pedido
            </div>
            <div className="flex-1 py-1.5 text-center text-[12px] font-medium text-[#0086C9]">
              Modificar Datos
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ message }: MessageBubbleProps) => {
  const isAgent = message.role === "assistant";
  const isTemplate = message.message_type === "template";
  const timeStr = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  if (isTemplate) {
    return <TemplateBubble message={message} />;
  }

  const isButtonResponse =
    message.role === "user" &&
    ["Confirmar", "Confirmar Pedido", "Modificar orden", "Modificar Datos"].includes(message.content);

  return (
    <div className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] text-sm leading-[1.5] shadow-sm ${
          isAgent
            ? "bg-[#D9FDD3] text-[#111B21] rounded-lg rounded-tr-none"
            : "bg-white text-[#111B21] rounded-lg rounded-tl-none"
        } ${isButtonResponse ? "px-3 py-1.5" : "px-3 py-2"} ${
          message.status === "sending" ? "opacity-70" : ""
        }`}
      >
        {isButtonResponse ? (
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-medium ${
                message.content === "Confirmar" || message.content === "Confirmar Pedido"
                  ? "text-emerald-600"
                  : "text-sky-600"
              }`}
            >
              {message.content === "Confirmar" || message.content === "Confirmar Pedido" ? "✅" : "✏️"} {message.content}
            </span>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        )}
        <div className={`flex items-center gap-1 mt-0.5 ${isAgent ? "justify-end" : "justify-start"}`}>
          <span className="text-[10px] text-[#667781]">{timeStr}</span>
          {isAgent && <MessageStatus status={message.status} />}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
